<template>
  <v-container class="d-block" fill-height fluid>
    <div class="pa-5">
      <v-btn color="primary" @click="openDialog">
        <v-icon>mdi-plus</v-icon>
        {{ $t('label.newUser') }}
      </v-btn>

      <v-dialog v-model="dialog" width="50%" max-width="600px" persistent>
        <v-card>
          <v-card-title class="headline grey lighten-2 pb-3">
            {{ $t('label.newUser') }}
          </v-card-title>

          <v-alert v-model="errorAlert" type="error" dismissible class="ma-3">
            {{ errorMessage }}
          </v-alert>

          <v-card-text class="pt-4">
            <v-form ref="form" autocomplete="off">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="userData.username" :label="$t('label.username')" outlined dense
                                required></v-text-field>
                  <v-text-field v-model="userData.password" :label="$t('label.password')" type="password" outlined dense
                                required></v-text-field>
                  <v-select
                      v-model="userData.groups"
                      :label="$t('label.groups')"
                      :items="groups"
                      item-text="groupDes"
                      item-value="id"
                      return-object
                      multiple
                      outlined
                      dense
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field v-model="userData.email" :label="$t('label.email')" type="email" outlined dense
                                required></v-text-field>
                  <v-text-field v-model="userData.repeatPassword" :label="$t('label.repeatPassword')" type="password"
                                outlined dense required></v-text-field>
                  <v-select
                      v-model="selectedCompany"
                      :items="companies"
                      item-text="name"
                      item-key="id"
                      return-object
                      :label="$t('label.organization')"
                      outlined
                      dense
                  />
                </v-col>
              </v-row>

              <div class="mt-2">
                <div class="subtitle-2 mb-2 text--secondary">Roles Assignments</div>
                <v-row no-gutters>
                  <v-col cols="12" sm="6" md="4">
                    <v-checkbox v-model="selectedRoles" value="shipper" label="Shipper"
                                :disabled="!selectedCompany?.isCustomer" dense class="mt-0"/>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-checkbox v-model="selectedRoles" value="consignee" label="Consignee"
                                :disabled="!selectedCompany?.isCustomer" dense class="mt-0"/>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-checkbox v-model="selectedRoles" value="clientFrom" label="Client From"
                                :disabled="!selectedCompany?.isCustomer" dense class="mt-0"/>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-checkbox v-model="selectedRoles" value="clientTo" label="Client To"
                                :disabled="!selectedCompany?.isCustomer" dense class="mt-0"/>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-checkbox v-model="selectedRoles" value="foreignAgent" label="Foreign Agent"
                                :disabled="!selectedCompany?.isSupplier" dense class="mt-0"/>
                  </v-col>
                </v-row>
              </div>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-3">
            <v-spacer></v-spacer>
            <v-btn @click="closeDialog">{{ $t('label.cancel') }}</v-btn>
            <v-btn color="primary" class="px-4" @click="saveUser">{{ $t('label.save') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <v-progress-linear
        indeterminate
        color="primary"
        class="mb-1"
        v-if="loading"
    />
    <v-expansion-panels v-model="panel" width="100%" class="align-self-start">
      <v-expansion-panel v-for="(user, i) in users" :key="i">
        <v-expansion-panel-header disable-icon-rotate class="px-3 py-0">
          <template v-slot:actions v-if="panel == i">
            <v-btn color="red" fab small @click.stop="confirmDelete">
              <v-icon size="25" color="white">mdi-delete</v-icon>
            </v-btn>

            <v-dialog v-model="deleteDialog" max-width="500px">
              <v-card>
                <v-card-title class="headline">{{ $t('label.confirmDelete') }}</v-card-title>
                <v-card-text>{{ $t('message.confirmDeletion') }}</v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text @click="deleteDialog = false">{{ $t('label.cancel') }}</v-btn>
                  <v-btn color="red" class="white--text" @click="deleteUser(user)">{{ $t('label.delete') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-btn fab small color="success" @click.stop="updateUser(user)">
              <v-icon size="25" color="white">mdi-content-save</v-icon>
            </v-btn>
          </template>

          <v-row class="ma-0">
            <v-col class="d-flex align-center pa-0">
              <span>{{ user.username }}</span>
            </v-col>
            <v-col class="d-flex align-center pa-0">
              <span>{{ user.email }}</span>
            </v-col>
            <v-col class="pa-0">
              <v-chip
                  class="ma-2 pa-2 text-overline"
                  color="primary"
                  small
                  v-for="(element, k) in user.groups"
                  :key="k"
              >
                <v-icon small class="pr-1">{{ getIcon(element.groupDes) }}</v-icon>
                {{ element.groupDes }}
              </v-chip>
            </v-col>
          </v-row>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <v-row class="ma-0">
            <v-col class="pa-0 pl-3">
              <span class="text-overline primary--text">{{ $t('label.settings') }}</span>
            </v-col>
          </v-row>
          <v-divider class="mx-2 mt-0 mb-4"/>

          <v-row class="mx-0">
            <v-col cols="12" sm="6" md="4" class="pa-2">
              <v-text-field v-model="user.email" :label="$t('label.email')"  autocomplete="off" outlined dense></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="pa-2">
              <v-text-field v-model="user.password" :label="$t('label.password')" autocomplete="new-password" type="password" outlined dense></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="pa-2">
              <v-select v-model="user.language" :items="languageItems" :label="$t('label.language')" item-text="text"
                        @change="changeLanguage" return-object prepend-inner-icon="mdi-translate" outlined dense/>
            </v-col>
          </v-row>
          <v-row class="mx-0">
            <v-col cols="12" sm="6" md="4" class="pa-2">
              <v-select v-model="user.groups" :items="groups" :label="$t('label.groups')" item-text="groupDes"
                        item-value="id" return-object prepend-inner-icon="mdi-account-group" multiple outlined dense/>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="pa-2">
              <v-select v-model="user.selectedCompany" :items="companies" item-text="name" item-key="id" return-object
                        :label="$t('label.organization')" prepend-inner-icon="mdi-domain" outlined dense/>
            </v-col>
          </v-row>

          <div class="mx-2 mt-2">
            <div class="subtitle-2 mb-1 text--secondary">Roles Assignments</div>
            <v-row no-gutters>
              <v-col cols="12" sm="4" md="2">
                <v-checkbox v-model="user.selectedRoles" value="shipper" label="Shipper"
                            :disabled="!user.selectedCompany?.isCustomer" dense class="mt-0"/>
              </v-col>
              <v-col cols="12" sm="4" md="2">
                <v-checkbox v-model="user.selectedRoles" value="consignee" label="Consignee"
                            :disabled="!user.selectedCompany?.isCustomer" dense class="mt-0"/>
              </v-col>
              <v-col cols="12" sm="4" md="2">
                <v-checkbox v-model="user.selectedRoles" value="clientFrom" label="Client From"
                            :disabled="!user.selectedCompany?.isCustomer" dense class="mt-0"/>
              </v-col>
              <v-col cols="12" sm="4" md="2">
                <v-checkbox v-model="user.selectedRoles" value="clientTo" label="Client To"
                            :disabled="!user.selectedCompany?.isCustomer" dense class="mt-0"/>
              </v-col>
              <v-col cols="12" sm="4" md="2">
                <v-checkbox v-model="user.selectedRoles" value="foreignAgent" label="Foreign Agent"
                            :disabled="!user.selectedCompany?.isSupplier" dense class="mt-0"/>
              </v-col>
            </v-row>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>

<script>
import {userParametersService} from '@/services/userParameters.service.js'
import {usersManagerService} from '@/services/usersManager.service.js'
import {groupsService} from '@/services/groups.service.js'
import {customersService} from '@/services/customers.service.js'
import {suppliersService} from "../../services/suppliers.service";

export default {
  name: 'AdminSettingsExpress',
  props: [],
  components: {},
  data: () => ({
    languageItems: [],
    panel: null,
    users: [],
    groups: [],
    companies: [],
    currentIndex: null,
    currentUser: {},
    userParams: {
      language: '',
    },
    loading: false,
    dialog: false,
    deleteDialog: false,
    userData: {
      id: null,
      username: '',
      password: '',
      repeatPassword: '',
      email: '',
      language: '',
      groups: [],
      customers: [],
      shippers: [],
      consignees: [],
      foreignAgents: [],
      clientsTo: []
    },
    errorAlert: false,
    errorMessage: '',
    selectedCompany: null,
    selectedRoles: []

  }),
  watch: {
    async panel(index) {
      this.userParams = {}
      if (index == null || index == undefined) {
        this.currentUser = {}
        return
      }
      const rawUser = this._.cloneDeep(this.users[index])
      const mapped = this.mapUserForUpdate(rawUser)
      this.$set(this.users, index, mapped)
      this.currentUser = mapped
      this.currentIndex = index
      setTimeout(() => {
        this.getUserParameters(this.currentUser.id)
      }, 300)
    },
  },
  methods: {
    init() {
      const usersPromise = this.browseUsers() // capture the promise
      this.browseGroups()
      this.loadCompanies()
      return usersPromise
    },
    browseUsers() {
      this.loading = true
      return usersManagerService.browse(0, 100, {}, []).then((resp) => {
        this.users = resp.resultList //.sort()
        this.loading = false
      })
    },
    browseGroups() {
      groupsService.browse(0, 100, {}, []).then((resp) => {
        this.groups = resp.resultList
      })
    },
    async loadCompanies() {
      const customerRequest = {
        offset: 0,
        limit: 1000,
        filter: {},
        sortingList: [
          {column: "customerName", direction: "ASC"}
        ]
      }

      const supplierRequest = {
        offset: 0,
        limit: 1000,
        filter: {},
        sortingList: [
          {column: "supplierName", direction: "ASC"}
        ]
      }

      const [customers, suppliers] = await Promise.all([
        customersService.browseInLike(customerRequest),
        suppliersService.browseLike(supplierRequest)
      ])

      const companies = new Map()

      customers.forEach(c => {
        companies.set(c.customerId, {
          id: c.customerId,
          name: c.customerName,
          isCustomer: true,
          isSupplier: false,
          customer: c,
          supplier: null
        })
      })

      suppliers.forEach(s => {
        const existing = companies.get(s.supplierId)

        if (existing) {
          existing.isSupplier = true
          existing.supplier = s
        } else {
          companies.set(s.supplierId, {
            id: s.supplierId,
            name: s.supplierName,
            isCustomer: false,
            isSupplier: true,
            customer: null,
            supplier: s
          })
        }
      })

      this.companies = [...companies.values()]
          .sort((a, b) => a.name.localeCompare(b.name))
    },
    getUserParameters(userId) {
      return userParametersService.getAllByUser(userId).then((resp) => {
        this.userParams = {}
        resp.forEach((param) => {
          this.userParams[param.key.toLowerCase()] = param
        })
        if (!this.userParams.language) this.createLanguageParameter(userId)
        else this.languageItems = this.selectLanguageItems()
      })
    },
    createLanguageParameter(userId) {
      let userParam = {
        userId: userId,
        key: 'LANGUAGE',
        valueType: 'STRING',
        value: null,
      }
      return userParametersService
          .createUserParameter(userParam)
          .then((resp) => {
            this.userParams.language = resp
            this.languageItems = this.selectLanguageItems()
          })
    },
    selectLanguageItems() {
      return [
        {
          id: this.userParams.language.id,
          key: 'LANGUAGE',
          userId: this.currentUser.id,
          value: 'en',
          text: 'English',
          valueType: 'STRING',
        },
        {
          id: this.userParams.language.id,
          key: 'LANGUAGE',
          userId: this.currentUser.id,
          value: 'it',
          text: 'Italiano',
          valueType: 'STRING',
        },
      ]
    },
    openDialog() {
      this.dialog = true;
    },
    saveUser() {
      if (this.userData.password !== this.userData.repeatPassword) {
        this.errorMessage = 'Password and repeat password fields must match';
        this.errorAlert = true;
        return;
      }

      const company = this.selectedCompany;

      this.userData.shippers = this.selectedRoles.includes("shipper") && company?.customer ? [company.customer] : [];
      this.userData.consignees = this.selectedRoles.includes("consignee") && company?.customer ? [company.customer] : [];
      this.userData.customers = this.selectedRoles.includes("clientFrom") && company?.customer ? [company.customer] : [];
      this.userData.clientsTo = this.selectedRoles.includes("clientTo") && company?.customer ? [company.customer] : [];
      this.userData.foreignAgents = this.selectedRoles.includes("foreignAgent") && company?.supplier ? [company.supplier] : [];

      usersManagerService.createUser(this.userData)
          .then((response) => {
            this.closeDialog();
            this.init()
          }).catch(error => {
        this.errorMessage = 'User could not be created: ' + error;
        this.errorAlert = true;
      });
    },
    closeDialog() {
      this.dialog = false;
      this.userData = {
        id: null,
        username: '',
        password: '',
        repeatPassword: '',
        email: '',
        language: '',
        groups: [],
        customers: [],
        shippers: [],
        consignees: [],
        foreignAgents: [],
        clientsTo: []
      };
      this.selectedCompany = null;
      this.errorMessage = '';
      this.errorAlert = false;
     this.selectedRoles = [];
    },
    updateUser(user) {
      const company = user.selectedCompany;

      user.shippers = user.selectedRoles.includes("shipper") && company?.customer ? [company.customer] : [];
      user.consignees = user.selectedRoles.includes("consignee") && company?.customer ? [company.customer] : [];
      user.customers = user.selectedRoles.includes("clientFrom") && company?.customer ? [company.customer] : [];
      user.clientsTo = user.selectedRoles.includes("clientTo") && company?.customer ? [company.customer] : [];
      user.foreignAgents = user.selectedRoles.includes("foreignAgent") && company?.supplier ? [company.supplier] : [];

      usersManagerService.updateUser(user.id, user).then((resp) => {
        const updatedUser = this.mapUserForUpdate(resp)
        this.currentUser = updatedUser
        this.$set(this.users, this.currentIndex, updatedUser)
      })
    },
    mapUserForUpdate(backendUser) {
      const user = {
        ...backendUser,
        selectedCompany: null,
        selectedRoles: []
      };

      const companyId =
          backendUser.customers?.[0]?.customerId ??
          backendUser.shippers?.[0]?.customerId ??
          backendUser.consignees?.[0]?.customerId ??
          backendUser.clientsTo?.[0]?.customerId ??
          backendUser.foreignAgents?.[0]?.supplierId;

      if (companyId) {
        user.selectedCompany = this.companies.find(c => c.id === companyId) ?? null;
      }

      if (backendUser.shippers?.length) user.selectedRoles.push("shipper");
      if (backendUser.consignees?.length) user.selectedRoles.push("consignee");
      if (backendUser.clientsTo?.length) user.selectedRoles.push("clientTo");
      if (backendUser.foreignAgents?.length) user.selectedRoles.push("foreignAgent");
      if (backendUser.customers?.length) user.selectedRoles.push("clientFrom");

      return user;
    },
    deleteUser(user) {
      usersManagerService.deleteUser(user.id).then((resp) => {
        this.deleteDialog = false;
        this.panel = null;
        this.init();
      })
    },
    confirmDelete() {
      event.stopPropagation();
      this.deleteDialog = true;
    },
    getIcon(type) {
      switch (type) {
        case 'admin':
          return 'mdi-shield-crown'
        case 'air':
          return 'mdi-airplane'
        case 'sea':
          return 'mdi-ferry'
      }
    },
    changeLanguage(language) {
      let userParam = {
        id: this.userParams.language.id,
        userId: this.currentUser.id,
        key: 'LANGUAGE',
        valueType: 'STRING',
        value: language.value,
      }

      return userParametersService
          .updateUserParameter(this.userParams.language.id, userParam)
          .then((lang) => {
            this.userParams.language = lang
          })
    }
  },
  created() {
    this.init()
  }
}
</script>

<style scoped>
.v-expansion-panel::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}

.custom-table {
  width: 100%;
  height: 100%;
}

.custom-table::v-deep .v-data-footer {
  justify-content: right;
}

.custom-table::v-deep .v-data-footer__select {
  display: none;
}

.v-data-table::v-deep .v-input--selection-controls__ripple {
  height: 0 !important;
}

.v-data-table::v-deep tr {
  display: flex;
}

.v-data-table::v-deep td {
  display: flex;
  align-items: center !important;
  padding: 0.2rem !important;
}

.custom-table-td {
  display: flex;
  width: -webkit-fill-available;
  align-items: center !important;
  padding: 0.2rem !important;
}

.dropdown-menu {
  width: 100%;
  padding: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
</style>
